import React from "react";
import { siteContent } from "../content";
import Section from "../components/Section";
import SocialIcons from "../components/SocialIcons";

export default function Home({ onNavigate }) {
  const { brand, socials, contact, artists, concerts, gallery } = siteContent;

  const heroStats = [
    { label: "Concerts", value: "1000+", note: "Worldwide performances" },
    { label: "Songs", value: "400+", note: "Films, albums, bhajans" },
    { label: "Awards", value: "Honours", note: "Celebrated performances" }
  ];

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={brand.heroImage}
            alt="Sasmita & Sangram hero"
            className="h-full w-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/55 to-purple-900/45" />
          <div className="absolute -left-20 -top-20 h-72 w-72 rounded-full bg-amber-400/20 blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 lg:py-24 flex flex-col lg:flex-row gap-10 items-center">
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs font-semibold text-amber-200">
              Live Concert Duo
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight drop-shadow-2xl">
              {brand.name}
            </h1>
            <p className="text-xl md:text-2xl text-amber-200 font-semibold">
              {brand.tagLine}
            </p>
            <p className="text-gray-200 max-w-3xl">
              {brand.heroSubtext}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => onNavigate("/contact")}
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-semibold px-6 py-3 shadow-xl hover:-translate-y-0.5 hover:shadow-amber-400/40 transition"
              >
                {brand.primaryCta}
              </button>
              <button
                onClick={() => onNavigate("/highlights")}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white font-semibold px-6 py-3 hover:bg-white/10 transition"
              >
                View Highlights
              </button>
            </div>
            <SocialIcons socials={socials} />
          </div>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
            {heroStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-5 text-center shadow-2xl"
              >
                <div className="text-3xl font-extrabold text-amber-300">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold text-white">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-300 mt-1">{stat.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section
        eyebrow="Artists"
        title="Meet the duo"
        description="Voices that create a magical aura — from playback singing to unforgettable live shows."
      >
        <div className="grid gap-6 md:grid-cols-2">
          {artists.map((artist) => (
            <div
              key={artist.name}
              className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-xl"
            >
              <div className="flex items-center gap-4 mb-4">
                <img
                  src={artist.image}
                  alt={artist.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-amber-300"
                />
                <div>
                  <div className="text-white font-bold text-lg">{artist.name}</div>
                  <div className="text-amber-200 text-sm font-semibold">
                    {artist.role}
                  </div>
                </div>
              </div>
              <p className="text-gray-200 text-sm leading-relaxed">{artist.highlight}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => onNavigate("/artists")}
            className="rounded-full bg-white/10 border border-white/20 text-white font-semibold px-5 py-3 hover:bg-white/15 transition"
          >
            View full artist bios
          </button>
        </div>
      </Section>

      <Section
        eyebrow="Highlights"
        title="Signature performances & achievements"
        description="Film songs, viral Bollywood covers, and concerts for audiences across the globe."
      >
        <div className="grid gap-6 md:grid-cols-2">
          {artists.map((artist) => (
            <div
              key={`${artist.name}-highlight`}
              className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-6 shadow-xl"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="h-2 w-2 rounded-full bg-amber-300" />
                <h3 className="text-white font-semibold text-lg">{artist.name}</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-200">
                {artist.achievements.slice(0, 4).map((item) => (
                  <div
                    key={item}
                    className="rounded-xl bg-black/40 border border-white/5 px-3 py-2"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-amber-200/80 mb-2">
            Major Live Concerts
          </p>
          <p className="text-gray-200 max-w-4xl mx-auto">{concerts}</p>
        </div>
      </Section>

      <Section
        eyebrow="Gallery"
        title="Live moments"
        description="Snapshots from concerts, backstage, and stage-light magic."
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {gallery.map((src) => (
            <img
              key={src}
              src={src}
              alt="Gallery"
              className="rounded-2xl object-cover h-40 w-full border border-white/10 shadow-lg"
            />
          ))}
        </div>
      </Section>

      <Section>
        <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-amber-400/10 via-white/5 to-purple-500/10 px-6 py-10 text-center shadow-2xl">
          <p className="text-sm uppercase tracking-[0.2em] text-amber-200/80 mb-2">
            Book a show
          </p>
          <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
            Elevate your event with soulful performances
          </h3>
          <p className="text-gray-200 mb-6">
            Live concerts, playback singing, and virtual shows tailored for festivals, weddings, and corporate events.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={contact.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-green-500 text-white font-semibold px-5 py-3 shadow-lg hover:shadow-green-500/30 transition"
            >
              WhatsApp: {contact.whatsapp}
            </a>
            <button
              onClick={() => onNavigate("/contact")}
              className="rounded-full border border-white/20 text-white font-semibold px-5 py-3 hover:bg-white/10 transition"
            >
              {brand.primaryCta}
            </button>
          </div>
        </div>
      </Section>
    </div>
  );
}
